import type { CallId, Incident, IncidentId, PortalCall } from '../realtime/contracts';
import { ObservableStore } from './observableStore';
import { preserveUnchanged } from './structuralSharing';

export type IncidentDataState = 'loading' | 'loaded' | 'failed';

export type IncidentsSnapshot = {
  readonly dataState: IncidentDataState;
  readonly liveIncidents: readonly Incident[];
  readonly incidentsById: ReadonlyMap<IncidentId, Incident>;
  readonly notFoundIds: ReadonlySet<IncidentId>;
  readonly failedIds: ReadonlySet<IncidentId>;
};

const EMPTY: IncidentsSnapshot = {
  dataState: 'loading',
  liveIncidents: [],
  incidentsById: new Map(),
  notFoundIds: new Set(),
  failedIds: new Set(),
};

function byCreatedTimeDescending(left: Incident, right: Incident): number {
  return Date.parse(right.createdTimeUtc) - Date.parse(left.createdTimeUtc);
}

export class IncidentStore extends ObservableStore<IncidentsSnapshot> {
  public constructor() {
    super(EMPTY);
  }

  public static get emptySnapshot(): IncidentsSnapshot {
    return EMPTY;
  }

  public getById(incidentId: IncidentId): Incident | null {
    return this.getSnapshot().incidentsById.get(incidentId) ?? null;
  }

  public upsert(incident: Incident): void {
    const liveIds = this.liveIds();
    liveIds.add(incident.id);

    this.publishIncidents(this.including(incident), liveIds);
  }

  public keepOutsideTheLiveWindow(incident: Incident): void {
    this.publishIncidents(this.including(incident), this.liveIds());
  }

  public updateCall(call: PortalCall): void {
    const current = this.getSnapshot().incidentsById;
    let updated: Map<IncidentId, Incident> | null = null;

    current.forEach((incident, incidentId) => {
      const agencyViews = incident.agencyViews.map((view) => {
        const calls = view.calls.map((known) => {
          if (known.id !== call.id || known.realtimeRevision >= call.realtimeRevision) {
            return known;
          }

          return preserveUnchanged(known, call);
        });

        return calls.every((known, index) => known === view.calls[index])
          ? view
          : { ...view, calls };
      });

      if (agencyViews.every((view, index) => view === incident.agencyViews[index])) {
        return;
      }

      updated ??= new Map(current);
      updated.set(incidentId, { ...incident, agencyViews });
    });

    if (updated !== null) {
      this.publishIncidents(updated, this.liveIds());
    }
  }

  public replaceAll(live: readonly Incident[], outsideTheLiveWindow: readonly Incident[] = []): void {
    const updated = new Map<IncidentId, Incident>();
    for (const incident of [...live, ...outsideTheLiveWindow]) {
      updated.set(incident.id, this.newerOf(incident));
    }

    this.publishIncidents(updated, new Set(live.map((incident) => incident.id)));
  }

  public drop(incidentIds: readonly IncidentId[]): void {
    if (incidentIds.length === 0) {
      return;
    }

    const updated = new Map(this.getSnapshot().incidentsById);
    const liveIds = this.liveIds();
    incidentIds.forEach((incidentId) => {
      updated.delete(incidentId);
      liveIds.delete(incidentId);
    });

    this.publishIncidents(updated, liveIds);
  }

  private liveIds(): Set<IncidentId> {
    return new Set(this.getSnapshot().liveIncidents.map((incident) => incident.id));
  }

  private including(incident: Incident): ReadonlyMap<IncidentId, Incident> {
    const updated = new Map(this.getSnapshot().incidentsById);
    updated.set(incident.id, this.newerOf(incident));

    return updated;
  }

  private newerOf(incident: Incident): Incident {
    const known = this.getById(incident.id);
    if (known !== null && known.realtimeRevision >= incident.realtimeRevision) {
      return known;
    }

    return preserveUnchanged(known ?? undefined, this.preserveNewerCalls(known, incident));
  }

  private preserveNewerCalls(known: Incident | null, incident: Incident): Incident {
    if (known === null) {
      return incident;
    }

    const knownCalls = new Map<CallId, PortalCall>();
    known.agencyViews.forEach((view) => {
      view.calls.forEach((call) => {
        const previous = knownCalls.get(call.id);
        if (previous === undefined || previous.realtimeRevision < call.realtimeRevision) {
          knownCalls.set(call.id, call);
        }
      });
    });

    let changed = false;
    const agencyViews = incident.agencyViews.map((view) => {
      const calls = view.calls.map((call) => {
        const previous = knownCalls.get(call.id);
        if (previous === undefined || previous.realtimeRevision < call.realtimeRevision) {
          return call;
        }

        changed = true;
        return previous;
      });

      return calls.every((call, index) => call === view.calls[index])
        ? view
        : { ...view, calls };
    });

    return changed ? { ...incident, agencyViews } : incident;
  }

  public markFailed(): void {
    this.publish({ dataState: 'failed' });
  }

  public markNotFound(incidentId: IncidentId): void {
    this.publish({ notFoundIds: new Set([...this.getSnapshot().notFoundIds, incidentId]) });
  }

  public markReadFailed(incidentId: IncidentId): void {
    this.publish({ failedIds: new Set([...this.getSnapshot().failedIds, incidentId]) });
  }

  public forgetReadFailure(incidentId: IncidentId): void {
    const failedIds = new Set(this.getSnapshot().failedIds);
    if (failedIds.delete(incidentId)) {
      this.publish({ failedIds });
    }
  }

  private publishIncidents(
    incidentsById: ReadonlyMap<IncidentId, Incident>,
    liveIds: ReadonlySet<IncidentId>,
  ): void {
    const notFoundIds = new Set(this.getSnapshot().notFoundIds);
    const failedIds = new Set(this.getSnapshot().failedIds);
    incidentsById.forEach((_, incidentId) => {
      notFoundIds.delete(incidentId);
      failedIds.delete(incidentId);
    });

    this.publish({
      dataState: 'loaded',
      incidentsById,
      liveIncidents: [...incidentsById.values()]
        .filter((incident) => liveIds.has(incident.id))
        .sort(byCreatedTimeDescending),
      notFoundIds,
      failedIds,
    });
  }
}
